import { useEffect, useMemo } from 'react';

import {
  getDaysSince,
  getUpsellUsage,
  isPlanUpgrade,
  isWithinFirstMonth,
  resolveUpsellContext,
  UpsellContext,
  UpsellUsage,
  useProducts,
} from '@/entities/pricing';
import { useGetSpaceCredits, useGetSpaces } from '@/entities/teamspace';
import { useCurrentUser } from '@/entities/user/currentUser';

import { Subscription } from '@/shared/types/Billing';

import { UpsellPersistedState, useUpsellStorage } from './useUpsellStorage';

export type UpsellSnapshot = {
  context: UpsellContext;
  daysSincePurchase: number;
  isFirstMonth: boolean;
  isUsageReady: boolean;
  isWriteError: boolean;
  ownSpaceIds: string[];
  purchaseAt: string;
  storage: UpsellPersistedState;
  subscription: Subscription;
  usage: null | UpsellUsage;
  update: (patch: Partial<UpsellPersistedState>) => void;
};

type UseUpsellSnapshotArgs = {
  spaceId?: string;
  writesPurchase?: boolean;
};

export const useUpsellSnapshot = ({
  spaceId,
  writesPurchase = false,
}: UseUpsellSnapshotArgs = {}): null | undefined | UpsellSnapshot => {
  const { data: user } = useCurrentUser();
  const { products } = useProducts(true, { showErrors: false });
  const subscription = user?.subscription;
  const context = useMemo(() => resolveUpsellContext({ products, subscription }), [products, subscription]);
  const { data: spaces, isFetched: areSpacesFetched } = useGetSpaces(Boolean(context));
  const ownSpaces = useMemo(() => spaces?.filter((space) => space.owner_id === user?.id) ?? [], [spaces, user?.id]);
  const ownSpaceId = ownSpaces.find((space) => space.is_default)?.id ?? '';
  const ownSpaceIds = useMemo(() => ownSpaces.map((space) => space.id), [ownSpaces]);
  const creditsSpaceId = context ? spaceId || ownSpaceId : '';
  const { data: spaceCredits, isError: isCreditsError, isFetched: areCreditsFetched } = useGetSpaceCredits(creditsSpaceId);
  const isUsageReady = areSpacesFetched && (!creditsSpaceId || areCreditsFetched || isCreditsError);
  const { isError: isStorageError, isReady, isWriteError, state, update } = useUpsellStorage(Boolean(context));
  const ownCredits = subscription && spaceCredits?.subscription?.id === subscription.id ? spaceCredits.credits : undefined;
  const usedMinutes = ownCredits?.minutes.used;

  const { purchase } = state;
  const isPurchaseKnown =
    Boolean(subscription) &&
    purchase?.subscriptionId === subscription?.id &&
    purchase?.priceId === subscription?.plan.id;

  useEffect(() => {
    if (!writesPurchase || !context || !isReady || !subscription || isPurchaseKnown) return;

    update(({ purchase: saved }) => {
      if (saved?.subscriptionId === subscription.id && saved.priceId === subscription.plan.id) return {};

      const getPurchaseAt = () => {
        if (!saved || saved.subscriptionId !== subscription.id) return subscription.created;

        return isPlanUpgrade(products, saved.priceId, subscription.plan.id) ? new Date().toISOString() : saved.at;
      };

      return {
        purchase: {
          at: getPurchaseAt(),
          priceId: subscription.plan.id,
          subscriptionId: subscription.id,
        },
      };
    });
  }, [context, isPurchaseKnown, isReady, products, subscription, update, writesPurchase]);

  const period = subscription?.current_period_start;
  const basePeriod = state.usageBase?.period;

  useEffect(() => {
    if (!writesPurchase || !context || !isReady || !period || usedMinutes === undefined || basePeriod === period) return;

    update((current) => (current.usageBase?.period === period ? {} : { usageBase: { period, used: usedMinutes } }));
  }, [basePeriod, context, isReady, period, update, usedMinutes, writesPurchase]);

  const isAudienceKnown = Boolean(user) && Boolean(products);

  return useMemo(() => {
    if (!isAudienceKnown) return undefined;
    if (!context || !subscription || isStorageError) return null;
    if (!isReady || !isPurchaseKnown || !purchase) return undefined;

    const now = Date.now();
    const daysSincePurchase = getDaysSince(purchase.at, now);

    return {
      context,
      daysSincePurchase,
      isFirstMonth: isWithinFirstMonth(purchase.at, now),
      isUsageReady: isUsageReady && (usedMinutes === undefined || basePeriod === period),
      isWriteError,
      ownSpaceIds,
      purchaseAt: purchase.at,
      storage: state,
      subscription,
      usage: getUpsellUsage(subscription, ownCredits, state.usageBase),
      update,
    };
  }, [
    context,
    isAudienceKnown,
    isPurchaseKnown,
    isStorageError,
    isReady,
    isUsageReady,
    isWriteError,
    ownCredits,
    ownSpaceIds,
    period,
    purchase,
    basePeriod,
    state,
    subscription,
    update,
    usedMinutes,
  ]);
};
